<!--@format-->
<template>
    <div class="document-solutions-widget">
        <div class="widget-header">
            <h3>Add a Solution</h3>
            <div class="svg-container widget-close-button" @click="closeWidget">
                <widget-close-svg />
            </div>
        </div>
        <div class="content-wrapper">
            <document-add-solutions-step
                v-if="currentStep === 'addSolutions'"
                :loading="isLoadingSearch"
                :current-tab="currentTab"
                :account-id="accountId"
                :search-value="queryOptions.search"
                :applied-sort="queryOptions.sort"
                :applied-filters="queryOptions.filters"
                :no-records="noRecords"
                :no-items="noItems"
                :deal-id="dealId"
                :available-custom="availableCustomSolutions"
                :available-distributor="availableDistributorItems"
                :available-opportunity="availableOpportunitySolutions"
                :selected-solutions="selectedSolutions"
                :total-value="totalValue"
                @select:tab="setActiveTab"
                @add:item="handleAddSolution"
                @view:item="handleViewItemDetails"
                @remove:item="handleRemoveSolution"
                @edit:inline-item="handleEditLineItem"
                @query="handleQuery"
                @load-more="handleLoadMore"
            />
            <product-details-step
                v-if="currentStep === 'productDetails'"
                :product="selectedProduct"
                :is-connected="isConnected"
                :loading="isLoadingPrice"
                @show:leave-popup="showLeavePopup"
            />
            <product-qty-step
                v-if="currentStep === 'selectQty'"
                :product="selectedProduct"
                :is-connected="isConnected"
                :integrations="integrationsData"
                @update:price="updateProductPrice"
                @update:qty="updateProductQty"
            />
        </div>
        <div class="widget-footer">
            <div class="cancel-container">
                <app-button v-if="hasBackAction" class="back-button" @click="goBack">
                    {{ backLabel }}
                </app-button>
            </div>
            <div class="action-container">
                <app-button @click="goNext">
                    {{ nextLabel }}
                </app-button>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button');
const DocumentaddSolutionsStep = () =>
    import('@/components/sliding-widgets/document/solution-widget/document-add-solutions-step');
const ProductDetailsStep = () =>
    import('@/components/sliding-widgets/document/solution-widget/product-details-step');
const ProductQtyStep = () =>
    import('@/components/sliding-widgets/document/solution-widget/product-qty-step');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg.vue');

import EventBus from '@/scripts/event-bus';
import PopUpMixin from '@/mixins/pop-up-mixin';
import { SolutionBoardServiceMixin } from '@/mixins/board/solution-board-service-mixin';
import { SolutionService } from '@/scripts/services/solution/solution-service';
import _ from 'lodash';

export default {
    name: 'document-solutions-widget',

    components: {
        'app-button': AppButton,
        'document-add-solutions-step': DocumentaddSolutionsStep,
        'product-details-step': ProductDetailsStep,
        'product-qty-step': ProductQtyStep,
        'widget-close-svg': WidgetCloseSvg,
    },

    mixins: [PopUpMixin, SolutionBoardServiceMixin],

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            isLoadingSearch: false,
            isLoadingPrice: false,
            solutionImagePlaceholder: 'https://cdn.honeycrm.com/app/image-placeholder.svg',
            totalValue: 0,
            customSolutions: [],
            availableCustomSolutions: [],
            opportunitySolutions: [],
            availableOpportunitySolutions: [],
            integrationsData: [],
            distributorItems: [],
            availableDistributorItems: [],
            selectedProduct: null,
            selectedSolutions: [...this.widget.solutions],

            distributorsEnum: {
                DandH: 'd&h',
                IngramMicro: 'ingramMicro',
                Pax8: 'pax8',
                TDSynnex: 'tdSynnex',
            },

            queryOptions: {
                search: null,
                sort: null,
                filters: [],
                page: 1,
                itemsPerPage: 15,
            },

            knownErrors: {
                authorized: 'Unautorized',
                discontinued: 'Discontinued',
            },

            currentTab: 'Custom',
            currentStep: 'addSolutions',

            steps: {
                addSolutions: {
                    actions: {
                        nextAction: {
                            label: 'Add Solutions',
                            callback: () => {
                                this.save();
                            },
                        },

                        backAction: {
                            label: 'Cancel',
                            callback: () => {
                                this.closeWidget();
                            },
                        },
                    },
                },

                productDetails: {
                    actions: {
                        nextAction: {
                            label: 'Select Quantity',
                            callback: () => {
                                this.goToStep('selectQty');
                            },
                        },

                        backAction: {
                            label: 'Go Back',
                            callback: () => {
                                this.goToStep('addSolutions');
                            },
                        },
                    },
                },

                selectQty: {
                    actions: {
                        nextAction: {
                            label: 'Add Solution',
                            callback: () => {
                                this.addSolution(this.selectedProduct);
                            },
                        },

                        backAction: {
                            label: 'Go Back',
                            callback: () => {
                                this.goToStep('productDetails');
                            },
                        },
                    },
                },
            },
        };
    },

    computed: {
        accountId() {
            return this.$store.state.context.account.id;
        },

        clientId() {
            return this.$store.state.context.client.id;
        },

        baseUrl() {
            return this.$store.getters.baseUrl;
        },

        quoteId() {
            return this.widget.quoteId;
        },

        dealId() {
            return this.widget.dealId;
        },

        noRecords() {
            if (this.currentTab === 'Custom') {
                return !this.customSolutions.length;
            } else if (this.currentTab === 'Distributors') {
                return !this.distributorItems.length;
            }
            return !this.opportunitySolutions.length;
        },

        noItems() {
            return this.selectedSolutions.length === 0;
        },

        isConnected() {
            return (
                !_.isEmpty(this.integrationsData) &&
                this.integrationsData.some((integration) => integration.connected)
            );
        },

        hasBackAction() {
            return this.steps[this.currentStep].actions.backAction !== undefined;
        },

        goBack() {
            return this.steps[this.currentStep].actions.backAction.callback;
        },

        goNext() {
            return this.steps[this.currentStep].actions.nextAction.callback;
        },

        backLabel() {
            return this.steps[this.currentStep].actions.backAction.label;
        },

        nextLabel() {
            return this.steps[this.currentStep].actions.nextAction.label;
        },
    },

    watch: {
        selectedSolutions: {
            immediate: true,
            deep: true,
            handler(solutions) {
                this.calculateTotalValue(solutions);
            },
        },

        currentTab: {
            immediate: true,
            handler() {
                this.query();
            },
        },
    },

    created() {
        this.getDistributorsIntegrationData();
        if (this.dealId) {
            this.getOpportunitySolutions({}, true);
        }
        EventBus.$on('solution:update', this.query);
        EventBus.$on('solution:edit-inline-save', this.handleSaveInline);
    },

    beforeDestroy() {
        EventBus.$off('solution:update', this.query);
        EventBus.$off('solution:edit-inline-save', this.handleSaveInline);
    },

    methods: {
        setActiveTab(tab) {
            this.currentTab = tab;
        },

        resetResults() {
            this.queryOptions.page = 1;
            this.customSolutions = [];
            this.opportunitySolutions = [];
            this.distributorItems = [];
        },

        handleSaveInline(e) {
            const findIndex = this.selectedSolutions.findIndex((item) => item.id === e.id);
            this.selectedSolutions.splice(findIndex, 1, e);
        },

        query() {
            this.resetResults();
            this.getSolutions(this.queryOptions);
        },

        handleQuery({ option, value }) {
            if (option === 'filters') {
                this.manageFilters(value);
            } else {
                this.queryOptions[option] = value;
            }

            this.query();
        },

        manageFilters(filter) {
            let isFilterApplied = this.queryOptions.filters.some(({ id }) => id === filter.id);

            if (isFilterApplied) {
                this.queryOptions.filters = this.queryOptions.filters.filter(
                    ({ id }) => id !== filter.id,
                );
            } else {
                this.queryOptions.filters.push(filter);
            }
        },

        handleLoadMore() {
            this.queryOptions.page++;
            this.getSolutions(this.queryOptions, false);
        },

        getDistributorsIntegrationData() {
            this.isLoadingSearch = true;
            return new Promise((resolve, reject) => {
                this.$api
                    .get(`/service/Integrations?$filter=ClientId eq ${this.clientId}`)
                    .then(({ data: { value } }) => {
                        this.integrationsData = this.mapIntegrations(value);
                        resolve();
                    })
                    .catch((error) => {
                        reject();
                        this.$helpers.showFailNotification(
                            'An error occurred while fetching your integrations.',
                            { error },
                        );
                    })
                    .finally(() => {
                        this.isLoadingSearch = false;
                    });
            });
        },

        mapIntegrations(integrations) {
            const mappedIntegrations = integrations.map((integration) => {
                const integrationName = integration['@odata.type']
                    .split('.')
                    .slice(-1)[0]
                    .replace('Integration', '');

                const distributor = this.distributorsEnum[integrationName];
                if (distributor) {
                    return {
                        distributor,
                        enabled: integration[`${integrationName}IsEnabled`],
                        connected: integration[`${integrationName}IsConnected`],
                    };
                }
            });

            return _.compact(mappedIntegrations);
        },

        getSolutions(options = {}, showLoading = true) {
            if (this.currentTab === 'Custom') {
                this.getCustomSolutions(options, showLoading);
            } else if (this.currentTab === 'Opportunity') {
                this.getOpportunitySolutions(options, showLoading);
            } else {
                this.getDistributorSolutions(options, showLoading);
            }
        },

        getOpportunitySolutions(options = {}, showLoading) {
            this.isLoadingSearch = showLoading;
            SolutionService.getFromOpportunity(this.dealId, options)
                .then((response) => {
                    const opportunityResponse = response.data.value[0];

                    if (opportunityResponse && opportunityResponse.LineItems.length) {
                        const billingCycles = this.$helpers.getSolutionBillingCycles();
                        const oppSolutions = opportunityResponse.LineItems.map((lineItem) => {
                            const billingCycle = billingCycles.find(
                                (cycle) => cycle.value === lineItem.BillingCycle,
                            );
                            return {
                                id: lineItem.Id,
                                name: lineItem.Solution.Name,
                                type: 'opportunity',
                                description: lineItem.Solution.Description,
                                previewImageUrl:
                                    lineItem.Solution.Url || this.solutionImagePlaceholder,
                                isPreviewImageSvg: lineItem.Solution.Url ? false : true,
                                videoUrl: lineItem.Solution.VideoUrl,
                                videoThumbnail: lineItem.Solution.VideoThumbnailUrl,
                                billingCycle: billingCycle.display,
                                isBundle: !_.isEmpty(lineItem.Solution.Bundles),
                                bundles: this.mapSolutionBundles(lineItem.Solution.Bundles),
                                price: lineItem.UnitPrice,
                                qty: lineItem.Quantity,
                                discount: lineItem.UnitDiscount,
                                cost: lineItem.UnitCost,
                                term: this.getLineItemTerm(
                                    lineItem.BillingCycle,
                                    lineItem.TermInMonths,
                                ),
                                subtotal: 0,
                            };
                        });

                        this.opportunitySolutions = [...this.opportunitySolutions, ...oppSolutions];
                        this.getAvailableOpportunitySolutions();
                    }
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while fetching your opportunity solutions.',
                        { error },
                    );
                })
                .finally(() => {
                    this.isLoadingSearch = false;
                });
        },

        getLineItemTerm(billingCycle, TermInMonths) {
            const terms = {
                0: 1,
                1: TermInMonths,
                2: TermInMonths / 4,
                3: TermInMonths / 6,
                4: TermInMonths / 12,
            };
            return terms[billingCycle] || 1;
        },

        getAvailableOpportunitySolutions() {
            this.availableOpportunitySolutions = _.differenceBy(
                this.opportunitySolutions,
                this.selectedSolutions,
                'id',
            );
        },

        getCustomSolutions(options, showLoading) {
            this.isLoadingSearch = showLoading;
            this.dispatchGetSolutions({ options })
                .then(({ items }) => {
                    this.customSolutions = [
                        ...this.customSolutions,
                        ...this.asCustomSolutionItems(items),
                    ];
                    this.getAvailableCustomSolutions();
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while fetching your solutions.',
                        { error },
                    );
                })
                .finally(() => {
                    this.isLoadingSearch = false;
                });
        },

        createParamsForDistributors(options) {
            const params = [];

            if (options.search) {
                const encodedSearch = encodeURIComponent(options.search);
                params.push(`keywords=${encodedSearch}`);
            }

            if (!_.isEmpty(options.filters)) {
                options.filters.forEach(({ property, value }) =>
                    params.push(`${property}=${value}`),
                );
            }

            if (options.page) {
                params.push(`page=${options.page}`);
            }

            if (options.itemsPerPage) {
                params.push(`pageSize=${options.itemsPerPage}`);
            }

            return !_.isEmpty(params) ? params.join('&') : '';
        },

        getDistributorSolutions(options = {}, showLoading) {
            this.isLoadingSearch = showLoading;
            this.getDistributorSolutionsData(options)
                .then((items) => {
                    this.distributorItems = [...this.distributorItems, ...items];
                    this.getAvailableDistributorItems();
                    return items;
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while fetching solutions from distributors.',
                        { error },
                    );
                })
                .then((items) => {
                    if (this.isConnected) {
                        return this.getPricingAndAvailability(items);
                    }
                })
                .finally(() => {
                    this.isLoadingSearch = false;
                });
        },

        getDistributorSolutionsData(options = {}) {
            const params = this.createParamsForDistributors(options);
            const distributorService = `/api/distributors/products/${this.clientId}/search?${params}`;

            return new Promise((resolve, reject) => {
                this.$api
                    .get(distributorService)
                    .then(({ data: { Products } }) => {
                        resolve(this.asDistributorItems(Products));
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'An error occurred while fetching distributor solutions data.',
                            { error },
                        );
                        reject();
                    });
            });
        },

        getPricingAndAvailability(items) {
            this.isLoadingPrice = true;
            const service = `/api/Distributors/Products/PriceAndAvailability/${this.clientId}`;
            const payload = items.map((item) => ({ ProductId: item.id, Catalog: item.catalog }));
            return new Promise((resolve, reject) => {
                this.$api
                    .post(service, payload)
                    .then(({ data }) => {
                        if (!_.isEmpty(data)) {
                            const priceAndAvailability = this.vendorsPricingAndAvailability(data);
                            this.udpateDistributorsItems(priceAndAvailability, items);
                            this.getAvailableDistributorItems();
                        }
                        resolve();
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'An error occurred while fetching distributor pricing and availability.',
                            { error },
                        );
                        reject();
                    })
                    .finally(() => {
                        this.isLoadingPrice = false;
                    });
            });
        },

        udpateDistributorsItems(priceAndAvailability, items) {
            const updatedItems = [...items];
            for (const productItem of updatedItems) {
                if (Array.isArray(productItem.vendors)) {
                    for (const vendor of productItem.vendors) {
                        const matchingPricingInfo = priceAndAvailability.find(
                            ({ vendorName, vendorPartNumber }) =>
                                vendorName === vendor.vendorName &&
                                vendorPartNumber === vendor.vendorPartNumber,
                        );

                        if (matchingPricingInfo) {
                            vendor.cost = matchingPricingInfo.cost;
                            vendor.onHand = matchingPricingInfo.onHand;
                            vendor.inStock = matchingPricingInfo.inStock;
                            vendor.error = matchingPricingInfo.error
                                ? this.getError(matchingPricingInfo.error)
                                : null;
                        }
                    }
                }
            }

            const updatedItemsMap = _.keyBy(updatedItems, 'id');
            _.forEach(this.distributorItems, (item) =>
                _.defaultsDeep({}, item, updatedItemsMap[item.id]),
            );
        },

        getAvailableCustomSolutions() {
            this.availableCustomSolutions = _.differenceBy(
                this.customSolutions,
                this.selectedSolutions,
                'id',
            );
        },

        asCustomSolutionItems(items) {
            return items.map((item) => ({
                id: item.id,
                name: item.name,
                type: 'custom',
                description: item.description,
                previewImageUrl: item.previewImageUrl
                    ? item.previewImageUrl
                    : this.solutionImagePlaceholder,

                isPreviewImageSvg: item.previewImageUrl ? item.isPreviewImageSvg : true,
                videoUrl: item.videoUrl,
                videoThumbnail: item.videoThumbnail,
                billingCycle: item.billingCycle,
                isBundle: !_.isEmpty(item.bundles),
                bundles: item.bundles,
                price: item.unitPrice,
                cost: item.unitCost,
                qty: 1,
                discount: 0,
                term: 1,
                subtotal: item.unitPrice,
            }));
        },

        getAvailableDistributorItems() {
            this.availableDistributorItems = _.differenceBy(
                this.distributorItems,
                this.selectedSolutions,
                'id',
            );
        },

        capitaLizeKey(key) {
            const words = key.match(/[A-Za-z][a-z]*/g);
            const capitalizedKey = words
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');
            return capitalizedKey;
        },

        hasStock(vendors = []) {
            return vendors ? vendors.some((vendor) => vendor.inStock) : false;
        },

        mapAttributes(attributes = {}) {
            let mappedAttributes = [];
            for (const key in attributes) {
                mappedAttributes.push({
                    label: this.capitaLizeKey(key),
                    value: attributes[key],
                });
            }

            return mappedAttributes;
        },

        getError(errorMessage) {
            const lowerCaseMessage = errorMessage.toLowerCase();
            let error = null;
            for (const knownError in this.knownErrors) {
                if (lowerCaseMessage.includes(knownError)) {
                    error = this.knownErrors[knownError];
                    break;
                }

                error = 'Unavailable';
            }

            return error;
        },

        asDistributorItems(items) {
            return items.map((item) => ({
                id: item.Id,
                name: item.Name,
                type: 'distributor',
                previewImageUrl: item.Image || this.solutionImagePlaceholder,
                isPreviewImageSvg: item.Image ? false : true,
                description: item.Description,
                catalog: item.Catalog,
                segment: item.Segment,
                category: item.Category,
                error: item.Error || null,
                msrp: item.Msrp || 0,
                inStock: this.hasStock(item.Vendors),
                attributes: this.mapAttributes(item.Attributes),
                manufacturer: item.Manufacturer,
                manufacturerPart: item.ManufacturerPartNumber,
                vendors: item.Vendors,
                qty: 1,
                discount: 0,
                cost: 0,
                billingCycle: 'One-time',
                price: item.Msrp || 0,
                subtotal: item.Msrp || 0,
                videoUrl: null,
                term: null,
            }));
        },

        vendorsPricingAndAvailability(data = []) {
            return data.flatMap(({ VendorName, Items }) =>
                Items.map((Item) => {
                    return {
                        vendorName: VendorName,
                        vendorPartNumber: Item.vendorPartNumber,
                        cost: Item.cost,
                        onHand: Item.onHand,
                        error: Item.error,
                        inStock: Boolean(Item.onHand),
                    };
                }),
            );
        },

        removeHtmlTags(input) {
            return input.replace(/<[^>]*>/g, '');
        },

        showLeavePopup() {
            const actions = {
                confirm: {
                    label: 'Proceed',
                    action: this.handleUpdateIntegrations,
                },

                cancel: {
                    label: 'Cancel',
                },
            };

            const modalProps = {
                title: 'Are you sure?',
                content:
                    '<p>You will be redirected to the <strong>Integrations</strong> page. Any unsaved changes will be lost.</p>',

                actions,
            };

            this.showPopup(modalProps, 'alert-popup');
        },

        handleUpdateIntegrations() {
            this.closeWidget();
            this.$router.push(`${this.baseUrl}/Account/Integrations`);
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        goToStep(step) {
            this.currentStep = step;
        },

        updateProductPrice(value) {
            this.selectedProduct.price = value;
        },

        updateProductQty(value) {
            this.selectedProduct.qty = value;
        },

        addSolution(item) {
            this.handleAddSolution(item);
            this.goToStep('addSolutions');
        },

        handleAddSolution(item) {
            if (item.type === 'distributor' && item.description) {
                item.description = this.removeHtmlTags(item.description);
            }
            this.$set(item, 'viewConfig', _.cloneDeep(this.widget.viewConfig));
            this.selectedSolutions.push(item);
            this.getAvailableCustomSolutions();
            this.getAvailableDistributorItems();
            this.getAvailableOpportunitySolutions();
        },

        handleViewItemDetails(item) {
            this.selectedProduct = item;
            this.goToStep('productDetails');
        },

        handleRemoveSolution(position) {
            this.selectedSolutions.splice(position, 1);
            this.getAvailableCustomSolutions();
            this.getAvailableDistributorItems();
            this.getAvailableOpportunitySolutions();
        },

        handleEditLineItem(solution) {
            const widget = {
                name: 'inline-solution-widget',
                solution: _.cloneDeep(solution),
            };
            EventBus.$emit('open-widget', widget);
        },

        calculateTotalValue(items) {
            this.totalValue = items.reduce((acc, { price, qty }) => acc + price * qty, 0);
        },

        save() {
            if (this.quoteId) {
                EventBus.$emit('quote:update-solutions', {
                    quoteId: this.quoteId,
                    solutions: this.selectedSolutions,
                });
            } else {
                EventBus.$emit('solutions:update', {
                    solutions: this.selectedSolutions,
                });
            }
            this.closeWidget();
        },
    },
};
</script>

<style>
.document-solutions-widget .content-wrapper {
    padding: 0px !important;
}

/*Customize Scrollbar for everything inside solutions container */
.document-solutions-widget .content-wrapper *::-webkit-scrollbar {
    width: 16px;
}

.document-solutions-widget .content-wrapper *::-webkit-scrollbar-track {
    border-left: 1px solid #e9e9ef;
}

.document-solutions-widget .content-wrapper *::-webkit-scrollbar-thumb {
    background-color: #cac6cb;
    border-radius: 16px;
    border: 3px solid transparent;
    background-clip: content-box;
}
</style>
